import React, { useState, useMemo } from 'react';
import { useAssessment } from './SharedContext';
import {
    Search, BookOpen, Sparkles, FileText, Users, GraduationCap,
    ChevronRight, ChevronDown, ExternalLink, Download, Upload,
    AlertCircle, CheckCircle, Info, Lightbulb, Settings,
    Database, PlayCircle, Save, RefreshCw, Eye, Edit3,
    Star, Clock, Target, Award, BarChart3, FileSpreadsheet,
    ArrowRight, Zap, Shield, HelpCircle, Lock, Cloud,
    Timer, AlertTriangle, UserCheck, FileJson, Globe, Copy,
    Monitor, Smartphone, Tablet, Printer, Mail, Calendar,
    GitBranch, Layers, Package, Workflow, Cpu, HardDrive,
    Image, LogIn, Home, ScrollText, ChevronUp
} from 'lucide-react';

const HelpPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedSections, setExpandedSections] = useState({});
    const [selectedCategory, setSelectedCategory] = useState('overview');
    const { setActiveTab } = useAssessment();

    // Toggle section expansion
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // Navigation to specific tabs
    const navigateToTab = (tabId) => {
        setActiveTab(tabId);
    };

    // Component definitions
    const QuickActionButton = ({ icon: Icon, title, description, onClick, color = 'blue' }) => (
        <button
            onClick={onClick}
            className={`p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-left border border-${color}-200 hover:border-${color}-300 group`}
        >
            <Icon className={`h-6 w-6 text-${color}-600 mb-2 group-hover:scale-110 transition-transform duration-200`} />
            <h3 className="font-medium text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
        </button>
    );

    const ExampleBlock = ({ title, children, language = 'text', downloadable = false }) => (
        <div className="my-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-200 flex justify-between items-center">
                <span>{title}</span>
                {downloadable && (
                    <button
                        className="text-blue-600 hover:text-blue-800 text-xs"
                        onClick={() => alert('Download functionality would be implemented here')}
                    >
                        <Download className="h-4 w-4" />
                    </button>
                )}
            </div>
            <div className={`p-4 ${language === 'json' ? 'bg-gray-900 text-green-400 font-mono text-sm' : 'bg-white'}`}>
                {children}
            </div>
        </div>
    );

    const DiagramBlock = ({ title, type, children }) => (
        <div className="my-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-200">
                {title}
            </div>
            <div className="p-6 bg-white">
                {children}
            </div>
        </div>
    );

    const FeatureCard = ({ icon: Icon, title, description, features, color = 'blue' }) => (
        <div className={`p-6 border border-${color}-200 rounded-lg bg-${color}-50`}>
            <div className="flex items-center space-x-3 mb-4">
                <Icon className={`h-8 w-8 text-${color}-600`} />
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <p className="text-gray-600 text-sm">{description}</p>
                </div>
            </div>
            {features && (
                <ul className="space-y-2">
                    {features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-2">
                            <CheckCircle className={`h-4 w-4 text-${color}-600 mt-0.5 flex-shrink-0`} />
                            <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    // Enhanced help content structure with ALL latest features
    const helpSections = [
        {
            id: 'overview',
            title: 'GradingPilot Overview',
            icon: Monitor,
            color: 'blue',
            tabId: null,
            content: {
                description: 'GradingPilot Professional Suite v2.0 is a comprehensive educational assessment platform designed for modern educators. It combines AI-assisted content creation, professional rubric development, privacy-first student management, and efficient grading workflows.',
                keyFeatures: [
                    'Privacy-First Design with Session-Only Storage',
                    'Clerk Authentication for Secure Access',
                    'AI-Powered Rubric and Assignment Generation',
                    'Professional Logo Integration for Institutional Branding',
                    'Comprehensive Grade Book with Multi-Assignment Support',
                    'Dynamic Grading Policies for Different Program Types',
                    'Enhanced Excel Import with Validation',
                    'Real-Time Session Management with Auto-Expiry',
                    'Cross-Tab Data Sharing During Active Sessions',
                    'Professional Export Options (JSON, HTML, Excel)'
                ],
                workflow: [
                    { step: 1, title: 'Authentication', description: 'Secure login via Clerk authentication' },
                    { step: 2, title: 'Class Setup', description: 'Import student lists via Excel with validation' },
                    { step: 3, title: 'Content Creation', description: 'Generate assignments and rubrics using AI' },
                    { step: 4, title: 'Logo Integration', description: 'Upload institutional logos for professional branding' },
                    { step: 5, title: 'Policy Management', description: 'Configure grading policies for program types' },
                    { step: 6, title: 'Assessment', description: 'Grade students with comprehensive feedback' },
                    { step: 7, title: 'Grade Management', description: 'Track progress in comprehensive gradebook' },
                    { step: 8, title: 'Export & Privacy', description: 'Export data and maintain session privacy' }
                ],
                systemArchitecture: [
                    'Session-Based Storage (Maximum 1 Hour)',
                    'Browser-Only Data Processing',
                    'No Cloud Data Persistence',
                    'Manual Import/Export Required',
                    'Cross-Tab Session Sharing',
                    'Automatic Session Cleanup'
                ]
            }
        },
        {
            id: 'quick-guide',
            title: 'Quick Start Guide',
            icon: Zap,
            color: 'blue',
            tabId: null,
            content: {
                description: 'Get started with GradingPilot in minutes. Follow this step-by-step guide to set up your first grading session.',
                steps: [
                    {
                        number: 1,
                        title: 'Create Your Account & Sign In',
                        description: 'Access GradingPilot using secure Clerk authentication',
                        actions: [
                            'Navigate to GradingPilot and sign in with your credentials',
                            'Review the privacy notice about session-only data storage',
                            'Familiarize yourself with the tab navigation'
                        ],
                        tip: 'Your session data is stored locally and expires after 1 hour of inactivity. Always export your work!'
                    },
                    {
                        number: 2,
                        title: 'Upload Your School Logo (Optional)',
                        description: 'Brand your exported documents with institutional logo',
                        actions: [
                            'Navigate to Logo Settings tab',
                            'Upload a high-resolution PNG file (recommended)',
                            'Preview the logo integration',
                            'Confirm and save'
                        ],
                        tip: 'Logos are stored in session only and will need to be re-uploaded when starting a new session.'
                    },
                    {
                        number: 3,
                        title: 'Configure Grading Policies',
                        description: 'Set up grading scales for your institution and program type',
                        actions: [
                            'Go to Policy Manager tab',
                            'Select your program type (Diploma, Degree, Certificate)',
                            'Review or customize the grade scale',
                            'Configure GPA weights and passing thresholds',
                            'Save your policy configuration'
                        ],
                        tip: 'Default Conestoga College policies are pre-loaded. Customize them for your institution.'
                    },
                    {
                        number: 4,
                        title: 'Import Your Class List',
                        description: 'Add student information via Excel file',
                        actions: [
                            'Prepare an Excel file with student names, IDs, and emails',
                            'Test the import using Excel Import Test tab (recommended)',
                            'Navigate to Class Manager tab',
                            'Upload your validated Excel file',
                            'Review the imported student data',
                            'Confirm course metadata (name, code, instructor)'
                        ],
                        tip: 'Use the Excel Import Test tool first to validate your file format and catch any data issues.'
                    },
                    {
                        number: 5,
                        title: 'Create Assignment Description',
                        description: 'Generate professional assignment descriptions for your LMS',
                        actions: [
                            'Go to Assignment Details tab',
                            'Enter course and assignment details',
                            'Add CLOs/ULOs with proper numbering',
                            'Generate AI prompt for content creation',
                            'Create student-facing HTML document',
                            'Export for D2L or other LMS platforms'
                        ],
                        tip: 'The HTML export includes your school logo and can be directly uploaded to D2L.'
                    },
                    {
                        number: 6,
                        title: 'Build Your Rubric',
                        description: 'Create a comprehensive assessment rubric',
                        actions: [
                            'Option A: Use AI Rubric Prompt Generator to create a prompt, then import the JSON',
                            'Option B: Build rubric manually in Rubric Builder',
                            'Configure achievement levels (2-7 levels available)',
                            'Add assessment criteria with descriptions',
                            'Create feedback libraries for efficient grading',
                            'Validate and test the rubric',
                            'Export to Grading Tool'
                        ],
                        tip: 'Start with 4-7 achievement levels for most assignments. Use feedback libraries to speed up grading.'
                    },
                    {
                        number: 7,
                        title: 'Grade Your Students',
                        description: 'Assess student work using your rubric',
                        actions: [
                            'Navigate to Grading Tool tab',
                            'Load your rubric and class list',
                            'Select a student to grade',
                            'Choose achievement levels for each criterion',
                            'Add detailed feedback from libraries or custom text',
                            'Apply late policies if needed',
                            'Save draft or finalize the grade',
                            'Export individual student report (HTML/JSON)'
                        ],
                        tip: 'Plan for 5-10 minutes per student. Export your work every 30 minutes as backup.'
                    },
                    {
                        number: 8,
                        title: 'Manage Your Gradebook',
                        description: 'Track all assignments and calculate final grades',
                        actions: [
                            'Go to Grade Book tab',
                            'Import your class list',
                            'Create projects for each assignment',
                            'Import grade data from Grading Tool',
                            'Review statistical analysis',
                            'Calculate final grades',
                            'Export gradebook (JSON/Excel/HTML)'
                        ],
                        tip: 'The gradebook supports multiple assignments and provides statistical insights.'
                    },
                    {
                        number: 9,
                        title: 'Export and Backup Your Work',
                        description: 'Save your session data before closing',
                        actions: [
                            'Click Export Session in the tab navigation',
                            'Save the JSON file to your computer',
                            'Export individual reports and gradebooks',
                            'Clear browser session when finished',
                            'Re-import session data when you return'
                        ],
                        tip: 'CRITICAL: Always export your work! Data is NOT saved on servers and will be lost when session expires.'
                    }
                ],
                commonWorkflows: [
                    {
                        title: 'First-Time Setup',
                        duration: '15-20 minutes',
                        steps: ['Upload logo', 'Configure policies', 'Import class list', 'Test Excel import']
                    },
                    {
                        title: 'Create New Assignment',
                        duration: '20-30 minutes',
                        steps: ['Generate assignment description', 'Create AI rubric prompt', 'Build rubric', 'Validate rubric']
                    },
                    {
                        title: 'Grading Session',
                        duration: '5-10 min per student',
                        steps: ['Load rubric and class list', 'Grade students', 'Export reports', 'Export session backup']
                    },
                    {
                        title: 'End of Term',
                        duration: '30-45 minutes',
                        steps: ['Import all grade data', 'Calculate final grades', 'Generate reports', 'Export gradebook']
                    }
                ],
                proTips: [
                    'Always use the Excel Import Test tool before importing to Class Manager',
                    'Create comprehensive feedback libraries to speed up grading',
                    'Export your session data every 30 minutes during long grading sessions',
                    'Use the session expiration toggle to extend your working time if needed',
                    'Test your rubrics with sample grading before using them for real assessments',
                    'Keep backup copies of your exported JSON files for future use',
                    'Use the school logo feature for professional-looking exports',
                    'Review statistical reports in Grade Book to identify outliers or issues'
                ]
            }
        },
        {
            id: 'assignment-prompt-generator',
            title: 'Assignment Details',
            icon: BookOpen,
            color: 'orange',
            tabId: 'assignment-prompt-generator',
            content: {
                description: 'Generate comprehensive assignment prompts with AI assistance and export to HTML for D2L or other LMS platforms.',
                features: [
                    'AI-Powered Assignment Description Generation',
                    'CLO/ULO Management with Numbering System (1-15 with decimals)',
                    'Program Type Support (Diploma, Degree, Certificate)',
                    'Professional HTML Export for D2L Integration',
                    'Student-Facing HTML Generation',
                    'Institutional Logo Integration',
                    'Weight Percentage Calculations',
                    'Special Instructions and Requirements',
                    'Assessment Criteria Integration',
                    'Form Data Persistence During Session'
                ],
                workflow: `1. Set Course Information (Code, Name, Instructor)
2. Define Assignment Details (Title, Type, Weight)
3. Configure CLOs/ULOs with Numbering
4. Generate AI Prompt for Content Creation
5. Create Professional HTML Output
6. Export for LMS Integration`,
                examplePrompt: `Generate a comprehensive assignment description for:
Course: [Course Code] - [Course Name]
Assignment: [Assignment Title]
Program: [Program Type - Level]
Weight: [Percentage]%
Duration: [Timeframe]

Include clear instructions, assessment criteria, and submission requirements.`
            }
        },
        {
            id: 'ai-prompt-generator',
            title: 'AI Rubric Prompt Generator',
            icon: Sparkles,
            color: 'blue',
            tabId: 'ai-prompt-generator',
            content: {
                description: 'Create detailed AI prompts to generate professional rubrics with customizable achievement levels and criteria.',
                features: [
                    'Multiple Achievement Level Templates (2-7 levels)',
                    'Assignment Data Import from Assignment Generator',
                    'CLO/ULO Integration with Validation',
                    'Custom Criteria or AI-Suggested Options',
                    'Weight Percentage Preservation',
                    'Student Population Considerations',
                    'Special Considerations Support',
                    'JSON Output Format for Rubric Builder',
                    'Program Type Alignment',
                    'Educational Research-Based Templates'
                ],
                achievementLevels: [
                    { level: 2, name: 'Binary (Unacceptable/Acceptable)' },
                    { level: 3, name: 'Three-Point (Unacceptable/Developing/Acceptable)' },
                    { level: 4, name: 'Four-Point (Unacceptable/Developing/Acceptable/Exemplary)' },
                    { level: 5, name: 'Five-Point Scale with Proficient' },
                    { level: 6, name: 'Six-Point Scale with Outstanding' },
                    { level: 7, name: 'Seven-Point Scale with Exceptional (Default)' }
                ],
                workflow: `1. Import Assignment Data (or enter manually)
2. Select Achievement Level Template
3. Choose Criteria Type (AI-suggested or custom)
4. Configure Learning Objectives
5. Add Special Considerations
6. Generate AI Prompt
7. Process with AI Tool (ChatGPT, Claude, etc.)
8. Import JSON into Rubric Builder`
            }
        },
        {
            id: 'rubric-creator',
            title: 'Rubric Builder',
            icon: FileText,
            color: 'purple',
            tabId: 'rubric-creator',
            content: {
                description: 'Create, edit, and manage professional assessment rubrics with dynamic point systems and feedback libraries.',
                features: [
                    {
                        category: 'Core Functionality',
                        items: [
                            'Dynamic Achievement Level Configuration (2-7 levels)',
                            'Flexible Point System (Multiplier or Custom)',
                            'Rich Text Editing for Descriptions',
                            'Feedback Library Management',
                            'JSON Import/Export Capabilities',
                            'Professional HTML Export for Students'
                        ]
                    },
                    {
                        category: 'Advanced Features',
                        items: [
                            'Rubric Validation and Error Checking',
                            'Automatic Point Calculation',
                            'Criterion Reordering and Management',
                            'Level Description Templates',
                            'Export Integration with Grading Tool',
                            'Visual Level Preview with Colors'
                        ]
                    }
                ],
                pointSystems: [
                    { type: 'Multiplier-Based', description: 'Automatic calculation using level multipliers (0x to 1.25x)' },
                    { type: 'Custom Points', description: 'Manual point ranges for each criterion level' }
                ],
                workflow: `1. Set Assignment Information (Title, Points, Passing Threshold)
2. Configure Achievement Levels
3. Add Assessment Criteria
4. Define Level Descriptions
5. Create Feedback Libraries
6. Validate and Test Rubric
7. Export to Grading Tool or JSON`,
                exportOptions: [
                    { name: 'JSON for Grading', description: 'Machine-readable format for grading tool', content: 'Complete rubric structure with all data' },
                    { name: 'Student HTML', description: 'Professional presentation for students', content: 'Clean, formatted rubric without feedback libraries' }
                ]
            }
        },
        {
            id: 'class-manager',
            title: 'Class Manager',
            icon: Users,
            color: 'indigo',
            tabId: 'class-manager',
            content: {
                description: 'Import and manage student class lists with comprehensive Excel parsing and validation.',
                features: [
                    'Enhanced Excel Import (.xls/.xlsx)',
                    'Automatic Data Validation and Quality Scoring',
                    'Course Metadata Extraction',
                    'Program Type Detection',
                    'Student Data Normalization',
                    'Privacy-Compliant Data Handling',
                    'Session-Only Storage',
                    'Manual Student Entry Options',
                    'Grading Session Initialization',
                    'Cross-Tab Data Sharing'
                ],
                excelFormat: `Required Excel Columns:
• Student Name (First Last or Last, First)
• Student ID/Number
• Email Address (optional)
• Program/Section (optional)

Course Metadata (auto-detected):
• Course Code
• Course Name
• Instructor/Professor
• Section/Program Type
• Term/Semester`,
                workflow: `1. Prepare Excel File with Student Data
2. Import via File Upload
3. Review Validation Results
4. Correct Any Data Issues
5. Confirm Import and Course Metadata
6. Initialize Grading Session
7. Export Session Data for Backup`,
                validation: [
                    'Student Name Format Verification',
                    'ID Number Uniqueness Check',
                    'Email Format Validation',
                    'Missing Data Detection',
                    'Duplicate Entry Prevention',
                    'Data Quality Scoring (0-100%)'
                ]
            }
        },
        {
            id: 'grading-tool',
            title: 'Grading Tool',
            icon: GraduationCap,
            color: 'green',
            tabId: 'grading-tool',
            content: {
                description: 'Comprehensive student grading interface with rubric-based assessment and session management.',
                features: [
                    {
                        category: 'Grading Interface',
                        items: [
                            'Rubric-Based Level Selection',
                            'Real-Time Point Calculation',
                            'Comprehensive Feedback System',
                            'Late Policy Integration',
                            'File Attachment Support',
                            'Video Link Integration'
                        ]
                    },
                    {
                        category: 'Session Management',
                        items: [
                            'Session Monitoring with Warnings',
                            'Auto-Save Draft Functionality',
                            'Student Navigation Controls',
                            'Progress Tracking',
                            'Session Extension Options',
                            'Privacy-First Data Handling'
                        ]
                    },
                    {
                        category: 'Export Options',
                        items: [
                            'Professional HTML Reports',
                            'JSON Grade Data Export',
                            'Email-Ready Summaries',
                            'LMS Integration Format',
                            'Logo-Branded Documents',
                            'Comprehensive Grade Summaries'
                        ]
                    }
                ],
                workflow: `1. Load Rubric and Class List
2. Select Student for Grading
3. Assess Each Criterion Level
4. Add Detailed Feedback
5. Apply Late Policies if Needed
6. Save Draft or Final Grade
7. Navigate to Next Student
8. Export Results`,
                sessionFeatures: [
                    'Maximum 1-Hour Session Duration',
                    'Session Warning at 5 Minutes Remaining',
                    'Extension Options Available',
                    'Auto-Cleanup on Expiry',
                    'Cross-Tab Session Sharing',
                    'Manual Export Required'
                ]
            }
        },
        {
            id: 'gradebook',
            title: 'Grade Book',
            icon: BookOpen,
            color: 'blue',
            tabId: 'gradebook',
            content: {
                description: 'Comprehensive gradebook for managing multiple assignments with statistical analysis and export capabilities.',
                features: [
                    {
                        category: 'Grade Management',
                        items: [
                            'Multi-Assignment Support',
                            'Dynamic Project Creation',
                            'Real-Time Grade Calculations',
                            'Statistical Analysis (Mean, Median, Distribution)',
                            'Letter Grade Conversion',
                            'Final Grade Computation'
                        ]
                    },
                    {
                        category: 'Data Import/Export',
                        items: [
                            'JSON Gradebook Import/Export',
                            'Excel Export with Formatting',
                            'HTML Report Generation',
                            'Class List Integration',
                            'Grade History Tracking',
                            'Privacy-Compliant Export'
                        ]
                    },
                    {
                        category: 'Privacy & Security',
                        items: [
                            'Session-Only Data Storage',
                            'No Cloud Persistence',
                            'Manual Export Required',
                            'Session Warning System',
                            'Automatic Data Cleanup',
                            'GDPR-Compliant Design'
                        ]
                    }
                ],
                workflow: `1. Import Class List
2. Create Projects/Assignments
3. Import Grades from Grading Tool
4. Review Statistical Analysis
5. Generate Reports
6. Export Gradebook Data
7. Clear Session Data`,
                exportFormats: [
                    { format: 'JSON', description: 'Complete gradebook data for re-import' },
                    { format: 'Excel', description: 'Formatted spreadsheet with statistics' },
                    { format: 'HTML', description: 'Professional report with institutional branding' }
                ]
            }
        },
        {
            id: 'policy-manager',
            title: 'Grading Policy Manager',
            icon: Settings,
            color: 'teal',
            tabId: 'policy-manager',
            content: {
                description: 'Configure and manage grading policies for different program types with institutional flexibility.',
                features: [
                    'Program Type Configuration (Diploma, Degree, Certificate)',
                    'Custom Grade Scale Definition',
                    'GPA Weight Assignment',
                    'Passing Grade Configuration',
                    'Institutional Policy Templates',
                    'Policy Import/Export',
                    'Multi-Campus Support',
                    'Late Policy Integration',
                    'Grade Conversion Tables',
                    'Academic Standards Compliance'
                ],
                programTypes: [
                    { type: 'Diploma', description: 'Applied learning programs with practical focus' },
                    { type: 'Degree', description: 'Academic programs with theoretical emphasis' },
                    { type: 'Certificate', description: 'Short-term skill-based programs' },
                    { type: 'Micro-Credential', description: 'Specialized competency programs' }
                ],
                workflow: `1. Select Program Type
2. Configure Grade Scale
3. Set GPA Weights
4. Define Passing Thresholds
5. Test Grade Calculations
6. Save Policy Configuration
7. Apply to Class Lists`,
                gradeScaleExample: `A+: 95-100% (4.0 GPA)
A:  90-94%  (4.0 GPA)
A-: 85-89%  (3.7 GPA)
B+: 80-84%  (3.3 GPA)
B:  75-79%  (3.0 GPA)
[Continue through F]`
            }
        },
        {
            id: 'logo-settings',
            title: 'School Logo Settings',
            icon: Image,
            color: 'pink',
            tabId: 'logo-settings',
            content: {
                description: 'Upload and manage institutional logos for professional branding across all exported documents.',
                features: [
                    'High-Resolution Logo Upload',
                    'Multiple Format Support (PNG, JPG, SVG)',
                    'Automatic Sizing and Optimization',
                    'Session-Based Storage',
                    'Preview Functionality',
                    'Export Integration',
                    'Brand Consistency Tools',
                    'Logo Quality Validation',
                    'Privacy-Compliant Storage',
                    'Professional Document Integration'
                ],
                supportedFormats: [
                    { format: 'PNG', description: 'Recommended for logos with transparency' },
                    { format: 'JPG', description: 'Good for photographic logos' },
                    { format: 'SVG', description: 'Vector format for scalability' }
                ],
                guidelines: [
                    'Use high-resolution images (minimum 200px wide)',
                    'PNG format with transparent background recommended',
                    'Logos automatically resized for export compatibility',
                    'Stored in session only - cleared on restart',
                    'Preview available before finalizing',
                    'Integrated into all professional exports'
                ],
                workflow: `1. Prepare High-Quality Logo File
2. Upload via Logo Settings Tab
3. Preview Logo Integration
4. Confirm and Save
5. Logo Applied to All Exports
6. Re-upload as Needed`,
                integrationPoints: [
                    'Assignment HTML Exports',
                    'Rubric Student Versions',
                    'Grading Report Headers',
                    'Gradebook Exports',
                    'Professional Email Summaries'
                ]
            }
        },
        {
            id: 'excel-import-test',
            title: 'Excel Import Testing',
            icon: FileSpreadsheet,
            color: 'emerald',
            tabId: 'excel-import-test',
            content: {
                description: 'Dedicated testing environment for Excel class list imports with comprehensive validation and debugging.',
                features: [
                    'Real-Time Import Validation',
                    'Data Quality Scoring',
                    'Error Detection and Reporting',
                    'Course Metadata Extraction',
                    'Student Data Normalization',
                    'Import Success Metrics',
                    'Detailed Debugging Information',
                    'File Size and Format Analysis',
                    'Privacy-Compliant Processing',
                    'Integration Testing'
                ],
                validationChecks: [
                    'File Format Verification (.xls/.xlsx)',
                    'Column Header Detection',
                    'Student Name Format Validation',
                    'ID Number Uniqueness',
                    'Email Format Checking',
                    'Course Metadata Extraction',
                    'Data Quality Scoring',
                    'Missing Data Analysis'
                ],
                workflow: `1. Upload Excel Test File
2. Review Automatic Validation
3. Check Data Quality Score
4. Analyze Error Reports
5. Correct Data Issues
6. Re-test if Necessary
7. Proceed to Class Manager`,
                testingMetrics: [
                    'File Processing Time',
                    'Data Quality Percentage',
                    'Error Detection Count',
                    'Course Metadata Completeness',
                    'Student Record Accuracy',
                    'Import Success Rate'
                ]
            }
        },
        {
            id: 'privacy-security',
            title: 'Privacy & Security',
            icon: Shield,
            color: 'green',
            content: {
                description: 'Comprehensive privacy protection and security measures implemented in GradingPilot. Designed for GDPR compliance and educational data protection.',
                privacyCompliance: [
                    'Session-Only Data Storage (Maximum 1 Hour)',
                    'No Cloud Data Persistence',
                    'Browser-Based Processing Only',
                    'Manual Import/Export Required',
                    'Automatic Session Cleanup',
                    'Cross-Tab Data Sharing During Session',
                    'No Data Tracking or Analytics',
                    'User-Controlled Data Management',
                    'GDPR-Compliant Design',
                    'Educational Data Privacy Standards'
                ],
                sessionManagement: [
                    'Maximum 1-hour session duration',
                    'Session warning at 5 minutes remaining',
                    'Optional session extension (one-time)',
                    'Automatic data cleanup on expiry',
                    'Session sharing across browser tabs',
                    'Manual session termination available'
                ],
                dataHandling: [
                    'All data stored in browser sessionStorage',
                    'No localStorage persistence',
                    'No external API calls for data storage',
                    'No automatic cloud backups',
                    'User-initiated export required',
                    'Complete data deletion on session end'
                ],
                complianceChecklist: [
                    'Data minimization principle applied',
                    'User consent for data processing',
                    'Right to data portability (export)',
                    'Right to erasure (session cleanup)',
                    'Data processing transparency',
                    'Security by design implementation',
                    'No third-party data sharing',
                    'Educational context compliance',
                    'Regular security assessment',
                    'Privacy-first development approach'
                ],
                userRights: [
                    { right: 'Right to Export', implementation: 'Full data export in JSON format available at any time' },
                    { right: 'Right to Deletion', implementation: 'Automatic data deletion after session expiry or manual cleanup' },
                    { right: 'Right to Portability', implementation: 'All data exportable in standard JSON format' },
                    { right: 'Data Transparency', implementation: 'Clear documentation of all data processing activities' },
                    { right: 'Processing Control', implementation: 'User controls all data import, processing, and export' }
                ],
                securityMeasures: [
                    'Clerk Authentication Integration',
                    'Secure Session Management',
                    'Input Validation and Sanitization',
                    'XSS Protection Measures',
                    'Content Security Policy',
                    'Secure File Upload Handling',
                    'Session Token Management',
                    'Browser Security Best Practices'
                ]
            }
        },
        {
            id: 'best-practices',
            title: 'Best Practices',
            icon: Award,
            color: 'yellow',
            content: {
                description: 'Educational best practices and workflow recommendations for optimal use of GradingPilot.',
                workflowRecommendations: [
                    {
                        phase: 'Preparation Phase',
                        tasks: [
                            'Set up institutional logo in Logo Settings',
                            'Configure grading policies for your program type',
                            'Prepare Excel class lists with proper formatting',
                            'Test Excel import with sample data'
                        ]
                    },
                    {
                        phase: 'Content Creation Phase',
                        tasks: [
                            'Create assignment descriptions with clear CLOs',
                            'Generate AI rubric prompts with proper criteria',
                            'Build comprehensive rubrics with feedback libraries',
                            'Validate all rubrics before deployment'
                        ]
                    },
                    {
                        phase: 'Assessment Phase',
                        tasks: [
                            'Import class lists and initialize grading sessions',
                            'Grade consistently using rubric levels',
                            'Provide comprehensive feedback for each criterion',
                            'Export individual reports regularly'
                        ]
                    },
                    {
                        phase: 'Data Management Phase',
                        tasks: [
                            'Export session data regularly for backup',
                            'Maintain gradebook with multiple assignments',
                            'Generate statistical reports for analysis',
                            'Clean up data at end of session'
                        ]
                    }
                ],
                timingTips: [
                    'Allow 5-10 minutes per student for comprehensive grading',
                    'Plan grading sessions within 45-minute blocks',
                    'Export data every 30 minutes as backup',
                    'Schedule grading during focused time blocks',
                    'Use feedback libraries to improve efficiency'
                ],
                qualityAssurance: [
                    'Always test rubrics with sample grading',
                    'Validate Excel imports before full processing',
                    'Review statistical reports for outliers',
                    'Maintain consistency across grading sessions',
                    'Document any custom grading policies used'
                ]
            }
        },
        {
            id: 'troubleshooting',
            title: 'Troubleshooting',
            icon: AlertTriangle,
            color: 'red',
            content: {
                description: 'Solutions for common issues and errors encountered while using GradingPilot.',
                commonIssues: [
                    {
                        issue: 'Session Expired During Grading',
                        symptoms: ['Session warning appears', 'Data not saving', 'Components not responding'],
                        solution: 'Export current work immediately, then extend session or restart with imported data.',
                        prevention: 'Monitor session timer and export data regularly during long grading sessions.'
                    },
                    {
                        issue: 'Excel Import Fails',
                        symptoms: ['Import error message', 'No student data loaded', 'Validation failures'],
                        solution: 'Check Excel format in Import Test tool, ensure proper column headers, verify student data format.',
                        prevention: 'Use Excel Import Test tool before importing to Class Manager. Follow required format guidelines.'
                    },
                    {
                        issue: 'Rubric Not Loading in Grading Tool',
                        symptoms: ['Empty rubric display', 'No criteria showing', 'Point calculation errors'],
                        solution: 'Verify rubric export from Rubric Builder, check JSON format validity, re-export if needed.',
                        prevention: 'Always validate rubric in Rubric Builder before exporting. Use built-in validation tools.'
                    },
                    {
                        issue: 'Logo Not Appearing in Exports',
                        symptoms: ['Missing logo in HTML exports', 'Logo preview not showing', 'Export format issues'],
                        solution: 'Re-upload logo in Logo Settings, verify file format (PNG recommended), check preview.',
                        prevention: 'Use high-resolution PNG files with transparent backgrounds. Test preview before grading.'
                    },
                    {
                        issue: 'Grade Calculations Incorrect',
                        symptoms: ['Wrong point totals', 'Percentage errors', 'Grade scale mismatches'],
                        solution: 'Verify grading policy configuration, check rubric point systems, recalculate manually.',
                        prevention: 'Configure grading policies correctly for program type. Test calculations with sample data.'
                    },
                    {
                        issue: 'Cross-Tab Data Not Syncing',
                        symptoms: ['Different data in multiple tabs', 'Changes not reflected', 'Session inconsistencies'],
                        solution: 'Refresh browser tabs, verify session is active, re-import data if needed.',
                        prevention: 'Work primarily in one tab, avoid multiple simultaneous editing sessions.'
                    }
                ],
                diagnosticTools: [
                    {
                        name: 'Session Status Check',
                        purpose: 'Verify session is active and timing',
                        howTo: 'Check session timer in top navigation bar or session warning messages'
                    },
                    {
                        name: 'Excel Import Test',
                        purpose: 'Validate Excel file format and data',
                        howTo: 'Use Excel Import Test tab before importing to Class Manager'
                    },
                    {
                        name: 'Rubric Validation',
                        purpose: 'Check rubric integrity and calculations',
                        howTo: 'Use validation tools in Rubric Builder before export'
                    },
                    {
                        name: 'Browser Console',
                        purpose: 'Advanced debugging for technical issues',
                        howTo: 'Press F12, check Console tab for error messages'
                    }
                ],
                emergencyProcedures: [
                    'If session expires: Export all available data immediately',
                    'If data appears lost: Check other browser tabs for session',
                    'If import fails: Use Excel Import Test for diagnosis',
                    'If export fails: Try different format or smaller data set',
                    'If performance issues: Clear browser cache and restart'
                ]
            }
        },
        {
            id: 'faq',
            title: 'Frequently Asked Questions',
            icon: HelpCircle,
            color: 'purple',
            content: {
                description: 'Quick answers to common questions about GradingPilot features, data privacy, and workflows.',
                categories: [
                    {
                        category: 'General Questions',
                        questions: [
                            {
                                q: 'Is my student data safe with GradingPilot?',
                                a: 'Absolutely. GradingPilot uses session-only storage, meaning NO student data is ever saved on our servers. All data exists only in your browser session and is automatically deleted when you close the tab or the session expires (1 hour). You must manually export data to save it.'
                            },
                            {
                                q: 'How long does a grading session last?',
                                a: 'Sessions last for a maximum of 1 hour by default. You\'ll receive a warning at 5 minutes remaining with an option to extend the session once. You can also toggle session expiration on/off in the top navigation bar.'
                            },
                            {
                                q: 'What happens to my data when the session expires?',
                                a: 'All session data is automatically deleted when your session expires or you close the browser. This is by design to protect student privacy. Always export your work using the Export button in the navigation bar before leaving.'
                            },
                            {
                                q: 'Can I use GradingPilot on multiple devices?',
                                a: 'Yes, but data doesn\'t sync between devices automatically. You must export your session from one device and import it on another. Session data can be shared across multiple tabs in the same browser.'
                            },
                            {
                                q: 'Is GradingPilot free to use?',
                                a: 'Pricing information and subscription details can be found on the GradingPilot website. The platform requires Clerk authentication for access.'
                            },
                            {
                                q: 'Does GradingPilot work offline?',
                                a: 'No, GradingPilot requires an internet connection for authentication and access. However, once loaded, most features work with minimal internet connectivity.'
                            }
                        ]
                    },
                    {
                        category: 'Data Import/Export',
                        questions: [
                            {
                                q: 'What Excel format do I need for class lists?',
                                a: 'Your Excel file should contain columns for Student Name, Student ID, and optionally Email and Program/Section. Use the Excel Import Test tool to validate your file format before importing. Both .xls and .xlsx formats are supported.'
                            },
                            {
                                q: 'How do I backup my grading work?',
                                a: 'Click the Export button in the top navigation bar to save your entire session as a JSON file. You can also export individual rubrics, gradebooks, and student reports from their respective tools. We recommend exporting every 30 minutes during long grading sessions.'
                            },
                            {
                                q: 'Can I import previously exported data?',
                                a: 'Yes! Use the Import button in the top navigation to load a previously exported session JSON file. This will restore your rubrics, class lists, and grading progress.'
                            },
                            {
                                q: 'What formats can I export grades in?',
                                a: 'You can export in multiple formats: JSON (for re-import), HTML (professional reports with logos), and Excel (gradebook data). Each tool has specific export options tailored to its function.'
                            },
                            {
                                q: 'Can I export to my Learning Management System (LMS)?',
                                a: 'Yes! The Assignment Details page creates HTML files that can be uploaded directly to D2L and other LMS platforms. Student reports can also be emailed or posted to your LMS.'
                            }
                        ]
                    },
                    {
                        category: 'Rubrics & Grading',
                        questions: [
                            {
                                q: 'How many achievement levels should I use?',
                                a: 'Most assignments work well with 4-7 achievement levels. Use fewer levels (2-3) for simple pass/fail assessments, and more levels (5-7) for complex projects requiring nuanced feedback. The default is 7 levels.'
                            },
                            {
                                q: 'Can I use the same rubric for multiple classes?',
                                a: 'Yes! Export your rubric as JSON and import it when grading different classes. You can also save multiple rubrics and reuse them across terms.'
                            },
                            {
                                q: 'What are feedback libraries and how do I use them?',
                                a: 'Feedback libraries are pre-written comments for each criterion that you can quickly select during grading. They save time by eliminating repetitive typing. Create them in the Rubric Builder under each criterion.'
                            },
                            {
                                q: 'How do late policies work?',
                                a: 'Late policies are configured in the Policy Manager by program type. When grading, you can apply late penalties which automatically adjust the student\'s grade based on your institution\'s policy.'
                            },
                            {
                                q: 'Can I grade without a rubric?',
                                a: 'The Grading Tool is designed to work with rubrics for consistency and comprehensive feedback. However, you can create a simple rubric with minimal criteria if you prefer a more open-ended approach.'
                            },
                            {
                                q: 'How do I handle group assignments?',
                                a: 'You can create groups in the Rubric Builder and assign multiple criteria to each group. When grading, you can apply the same feedback to all criteria in a group, useful for group projects.'
                            }
                        ]
                    },
                    {
                        category: 'AI Features',
                        questions: [
                            {
                                q: 'Does GradingPilot use AI to grade my students?',
                                a: 'No. GradingPilot does NOT automatically grade students using AI. The AI features only help you generate prompts for creating assignment descriptions and rubrics. You always maintain full control over all grading decisions.'
                            },
                            {
                                q: 'What AI tools can I use with the AI prompts?',
                                a: 'The AI Prompt Generator creates prompts compatible with ChatGPT, Claude, Gemini, or any other large language model. Copy the generated prompt and paste it into your preferred AI tool.'
                            },
                            {
                                q: 'Do I need an AI subscription to use GradingPilot?',
                                a: 'No. The AI prompt generators are optional features. You can create assignment descriptions and rubrics manually without using any AI tools.'
                            },
                            {
                                q: 'Can I customize the AI-generated content?',
                                a: 'Absolutely! The AI prompts are starting points. You can edit the generated content extensively in the Rubric Builder and Assignment Details page to match your specific needs.'
                            }
                        ]
                    },
                    {
                        category: 'Technical Issues',
                        questions: [
                            {
                                q: 'Why isn\'t my Excel file importing?',
                                a: 'Common issues include incorrect column names, missing required data, or file format problems. Use the Excel Import Test tool to diagnose the issue. Ensure your file has columns for Student Name and Student ID at minimum.'
                            },
                            {
                                q: 'My session expired while grading. Can I recover my work?',
                                a: 'If you didn\'t export before the session expired, the data cannot be recovered. This is by design for privacy. Always export your work regularly (every 30 minutes recommended) during long grading sessions.'
                            },
                            {
                                q: 'The logo isn\'t showing in my exports. What should I do?',
                                a: 'Make sure you uploaded the logo in Logo Settings and that it\'s still in your active session. Logos are session-only and need to be re-uploaded each session. Use PNG format with transparent background for best results.'
                            },
                            {
                                q: 'Can I use GradingPilot on mobile devices?',
                                a: 'While GradingPilot is responsive and works on tablets and large phones, we recommend using a desktop or laptop computer for the best experience, especially for creating rubrics and grading.'
                            },
                            {
                                q: 'Which browsers are supported?',
                                a: 'GradingPilot works best on modern browsers including Chrome, Firefox, Safari, and Edge. Make sure your browser is updated to the latest version for optimal performance.'
                            }
                        ]
                    },
                    {
                        category: 'Policies & Grades',
                        questions: [
                            {
                                q: 'What are the default grading policies?',
                                a: 'GradingPilot comes pre-loaded with Conestoga College grading policies for Diploma, Degree, and Certificate programs. You can customize these or create your own policies in the Policy Manager.'
                            },
                            {
                                q: 'Can I have different policies for different courses?',
                                a: 'Yes! Set the program type in the Class Manager when importing your class list, and the appropriate grading policy will be applied automatically.'
                            },
                            {
                                q: 'How are final grades calculated?',
                                a: 'In the Grade Book, final grades are calculated based on the weighted average of all assignments. You can configure assignment weights when creating rubrics and projects.'
                            },
                            {
                                q: 'Can I override calculated grades?',
                                a: 'Yes, the Grade Book allows manual adjustments to calculated grades if needed. All changes are tracked and can be exported for record-keeping.'
                            }
                        ]
                    }
                ]
            }
        }
    ];

    // Enhanced search with category filtering
    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return [];

        const query = searchQuery.toLowerCase();
        const results = [];

        helpSections.forEach(section => {
            // Search in section title and description
            if (section.title.toLowerCase().includes(query) ||
                section.content.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'section',
                    section: section,
                    matches: [section.title]
                });
            }

            // Search in subsections and content
            Object.entries(section.content).forEach(([key, value]) => {
                if (typeof value === 'string' && value.toLowerCase().includes(query)) {
                    results.push({
                        type: 'content',
                        section: section,
                        contentKey: key,
                        matches: [key]
                    });
                }

                if (Array.isArray(value)) {
                    value.forEach((item, index) => {
                        if (typeof item === 'object' && item.title?.toLowerCase().includes(query)) {
                            results.push({
                                type: 'item',
                                section: section,
                                item: item,
                                itemIndex: index
                            });
                        }
                    });
                }
            });
        });

        return results;
    }, [searchQuery]);

    // Category navigation with enhanced structure
    const categories = [
        {
            section: 'Getting Started',
            items: [
                { id: 'overview', name: 'Overview & Quick Start', icon: Monitor },
                { id: 'quick-guide', name: 'Quick Start Guide', icon: Zap },
            ]
        },
        {
            section: 'Core Features',
            items: [
                { id: 'assignment-prompt-generator', name: 'Assignment Details', icon: BookOpen },
                { id: 'ai-prompt-generator', name: 'AI Rubric Prompt', icon: Sparkles },
                { id: 'rubric-creator', name: 'Rubric Builder', icon: FileText },
                { id: 'class-manager', name: 'Class Manager', icon: Users },
                { id: 'grading-tool', name: 'Grading Tool', icon: GraduationCap },
                { id: 'gradebook', name: 'Grade Book', icon: BookOpen },
            ]
        },
        {
            section: 'Configuration',
            items: [
                { id: 'policy-manager', name: 'Policy Manager', icon: Settings },
                { id: 'logo-settings', name: 'Logo Settings', icon: Image },
                { id: 'excel-import-test', name: 'Excel Import Test', icon: FileSpreadsheet },
            ]
        },
        {
            section: 'Help & Resources',
            items: [
                { id: 'faq', name: 'FAQ', icon: HelpCircle },
                { id: 'troubleshooting', name: 'Troubleshooting', icon: AlertTriangle },
                { id: 'best-practices', name: 'Best Practices', icon: Award },
                { id: 'privacy-security', name: 'Privacy & Security', icon: Shield },
            ]
        }
    ];

    const selectedSection = helpSections.find(section => section.id === selectedCategory);

    return (
        <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <HelpCircle className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">GradingPilot Help Center</h1>
                            <p className="text-gray-600">Comprehensive guide to professional assessment tools</p>
                        </div>
                    </div>
                    <div className="text-sm text-gray-500">
                        Professional Suite v2.0
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                        type="text"
                        placeholder="Search help topics, features, or troubleshooting..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Search Results ({searchResults.length})</h3>
                        <div className="space-y-2">
                            {searchResults.slice(0, 5).map((result, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setSelectedCategory(result.section.id);
                                        setSearchQuery('');
                                    }}
                                    className="w-full text-left p-3 rounded-lg hover:bg-blue-50 border border-gray-200"
                                >
                                    <div className="flex items-center space-x-2">
                                        <result.section.icon className="h-4 w-4 text-blue-600" />
                                        <span className="font-medium text-gray-900">{result.section.title}</span>
                                        <ChevronRight className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">{result.section.content.description}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex gap-6">
                {/* Sidebar Navigation */}
                <div className="w-80 bg-white rounded-lg shadow-sm p-4 sticky top-6 self-start max-h-[calc(100vh-8rem)] overflow-y-auto">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Help Topics</h2>
                    <div className="space-y-4">
                        {categories.map((categoryGroup, groupIndex) => (
                            <div key={groupIndex} className="space-y-2">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2">
                                    {categoryGroup.section}
                                </h3>
                                <div className="space-y-1">
                                    {categoryGroup.items.map(category => {
                                        const Icon = category.icon;
                                        return (
                                            <button
                                                key={category.id}
                                                onClick={() => setSelectedCategory(category.id)}
                                                className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-all duration-200 ${selectedCategory === category.id
                                                        ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                                                        : 'hover:bg-gray-50 text-gray-700 hover:border hover:border-gray-200'
                                                    }`}
                                            >
                                                <Icon className="h-5 w-5 flex-shrink-0" />
                                                <span className="font-medium text-sm">{category.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 bg-white rounded-lg shadow-sm">
                    {selectedSection && (
                        <div className="p-6">
                            {/* Section Header */}
                            <div className="flex items-center justify-between pb-6 border-b border-gray-200">
                                <div className="flex items-center space-x-4">
                                    <div className={`w-12 h-12 bg-${selectedSection.color}-100 rounded-lg flex items-center justify-center`}>
                                        <selectedSection.icon className={`h-6 w-6 text-${selectedSection.color}-600`} />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">{selectedSection.title}</h2>
                                        <p className="text-gray-600">{selectedSection.content.description}</p>
                                    </div>
                                </div>
                                {selectedSection.tabId && (
                                    <button
                                        onClick={() => navigateToTab(selectedSection.tabId)}
                                        className={`flex items-center space-x-2 px-4 py-2 bg-${selectedSection.color}-600 text-white rounded-lg hover:bg-${selectedSection.color}-700 transition-colors duration-200`}
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        <span>Open Tool</span>
                                    </button>
                                )}
                            </div>

                            {/* Content Sections */}
                            <div className="space-y-8">
                                {/* Overview Section */}
                                {selectedSection.id === 'overview' && (
                                    <>
                                        {/* Key Features */}
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h3>
                                            <div className="grid md:grid-cols-2 gap-4">
                                                {selectedSection.content.keyFeatures.map((feature, index) => (
                                                    <div key={index} className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                                                        <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                                        <span className="text-blue-900">{feature}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* System Architecture */}
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Privacy-First Architecture</h3>
                                            <DiagramBlock title="System Architecture Overview" type="architecture">
                                                <div className="grid md:grid-cols-3 gap-4">
                                                    {selectedSection.content.systemArchitecture.map((item, index) => (
                                                        <div key={index} className="p-4 border border-blue-200 rounded-lg bg-blue-50 text-center">
                                                            <Shield className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                                                            <p className="text-sm font-medium text-blue-900">{item}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </DiagramBlock>
                                        </div>

                                        {/* Workflow */}
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Complete Workflow</h3>
                                            <DiagramBlock title="End-to-End Process" type="workflow">
                                                <div className="space-y-4">
                                                    {selectedSection.content.workflow.map((step, index) => (
                                                        <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                                                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                                                {step.step}
                                                            </div>
                                                            <div>
                                                                <h4 className="font-medium text-gray-900">{step.title}</h4>
                                                                <p className="text-gray-600 text-sm">{step.description}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </DiagramBlock>
                                        </div>

                                        {/* Quick Actions */}
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Start Actions</h3>
                                            <div className="grid md:grid-cols-3 gap-4">
                                                <QuickActionButton
                                                    icon={Users}
                                                    title="1. Import Class List"
                                                    description="Start by importing your student data"
                                                    onClick={() => navigateToTab('class-manager')}
                                                    color="indigo"
                                                />
                                                <QuickActionButton
                                                    icon={FileText}
                                                    title="2. Build Rubric"
                                                    description="Create professional assessment rubrics"
                                                    onClick={() => navigateToTab('rubric-creator')}
                                                    color="green"
                                                />
                                                <QuickActionButton
                                                    icon={GraduationCap}
                                                    title="3. Start Grading"
                                                    description="Grade assignments with comprehensive feedback"
                                                    onClick={() => navigateToTab('grading-tool')}
                                                    color="blue"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Quick Start Guide Section */}
                                {selectedSection.id === 'quick-guide' && (
                                    <>
                                        {/* Step-by-Step Guide */}
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Step-by-Step Setup Guide</h3>
                                            <div className="space-y-6">
                                                {selectedSection.content.steps.map((step, index) => (
                                                    <div key={index} className="border border-blue-200 rounded-lg overflow-hidden">
                                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-blue-200">
                                                            <div className="flex items-center space-x-4">
                                                                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0">
                                                                    {step.number}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h4 className="text-lg font-semibold text-gray-900">{step.title}</h4>
                                                                    <p className="text-gray-600 text-sm">{step.description}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="p-4 bg-white">
                                                            <div className="mb-3">
                                                                <h5 className="text-sm font-medium text-gray-700 mb-2">Actions:</h5>
                                                                <ul className="space-y-2">
                                                                    {step.actions.map((action, actionIndex) => (
                                                                        <li key={actionIndex} className="flex items-start space-x-2">
                                                                            <ArrowRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                                            <span className="text-sm text-gray-700">{action}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                                <div className="flex items-start space-x-2">
                                                                    <Lightbulb className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <strong className="text-yellow-800 text-sm">Tip:</strong>
                                                                        <p className="text-yellow-700 text-sm mt-1">{step.tip}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Common Workflows */}
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Common Workflows</h3>
                                            <div className="grid md:grid-cols-2 gap-4">
                                                {selectedSection.content.commonWorkflows.map((workflow, index) => (
                                                    <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <h4 className="font-semibold text-gray-900">{workflow.title}</h4>
                                                            <div className="flex items-center space-x-1 text-gray-600">
                                                                <Clock className="h-4 w-4" />
                                                                <span className="text-xs">{workflow.duration}</span>
                                                            </div>
                                                        </div>
                                                        <ul className="space-y-2">
                                                            {workflow.steps.map((step, stepIndex) => (
                                                                <li key={stepIndex} className="flex items-start space-x-2">
                                                                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                                    <span className="text-sm text-gray-700">{step}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Pro Tips */}
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Pro Tips</h3>
                                            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                                                <div className="flex items-start space-x-2 mb-3">
                                                    <Star className="h-5 w-5 text-purple-600 flex-shrink-0" />
                                                    <h4 className="font-semibold text-purple-900">Expert Recommendations</h4>
                                                </div>
                                                <div className="grid md:grid-cols-2 gap-3">
                                                    {selectedSection.content.proTips.map((tip, index) => (
                                                        <div key={index} className="flex items-start space-x-2 p-3 bg-white rounded border border-purple-100">
                                                            <Zap className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                                            <span className="text-sm text-gray-700">{tip}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* FAQ Section */}
                                {selectedSection.id === 'faq' && (
                                    <>
                                        <div className="space-y-6">
                                            {selectedSection.content.categories.map((category, catIndex) => (
                                                <div key={catIndex}>
                                                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{category.category}</h3>
                                                    <div className="space-y-4">
                                                        {category.questions.map((item, qIndex) => (
                                                            <div key={qIndex} className="border border-gray-200 rounded-lg">
                                                                <button
                                                                    onClick={() => toggleSection(`faq-${catIndex}-${qIndex}`)}
                                                                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 rounded-t-lg"
                                                                >
                                                                    <div className="flex items-center space-x-3 text-left">
                                                                        <HelpCircle className="h-5 w-5 text-purple-600 flex-shrink-0" />
                                                                        <span className="font-medium text-gray-900">{item.q}</span>
                                                                    </div>
                                                                    <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-200 flex-shrink-0 ${expandedSections[`faq-${catIndex}-${qIndex}`] ? 'rotate-180' : ''
                                                                        }`} />
                                                                </button>
                                                                {expandedSections[`faq-${catIndex}-${qIndex}`] && (
                                                                    <div className="p-4 bg-white border-t border-gray-200">
                                                                        <p className="text-gray-700 leading-relaxed">{item.a}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Still Have Questions */}
                                        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                                            <div className="flex items-center space-x-3 mb-4">
                                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <HelpCircle className="h-6 w-6 text-blue-600" />
                                                </div>
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-900">Still Have Questions?</h4>
                                                    <p className="text-gray-600 text-sm">We're here to help!</p>
                                                </div>
                                            </div>
                                            <div className="grid md:grid-cols-3 gap-4">
                                                <button className="flex flex-col items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200">
                                                    <Mail className="h-8 w-8 text-blue-600 mb-2" />
                                                    <span className="font-medium text-gray-900">Email Support</span>
                                                    <span className="text-xs text-gray-600">support@gradingpilot.com</span>
                                                </button>
                                                <button className="flex flex-col items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-md transition-all duration-200">
                                                    <BookOpen className="h-8 w-8 text-green-600 mb-2" />
                                                    <span className="font-medium text-gray-900">Documentation</span>
                                                    <span className="text-xs text-gray-600">View detailed guides</span>
                                                </button>
                                                <button className="flex flex-col items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all duration-200">
                                                    <PlayCircle className="h-8 w-8 text-purple-600 mb-2" />
                                                    <span className="font-medium text-gray-900">Video Tutorials</span>
                                                    <span className="text-xs text-gray-600">Watch how-to videos</span>
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Features Display */}
                                {selectedSection.content.features && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Features</h3>
                                        {/* Check if features are structured with categories */}
                                        {typeof selectedSection.content.features[0] === 'object' && selectedSection.content.features[0].category ? (
                                            // Render layout for categories and items (like in Grade Book)
                                            <div className="space-y-6">
                                                {selectedSection.content.features.map((featureSet, index) => (
                                                    <div key={index}>
                                                        <h4 className="text-lg font-medium text-gray-800 mb-3">{featureSet.category}</h4>
                                                        <div className="grid md:grid-cols-2 gap-3">
                                                            {featureSet.items.map((item, itemIndex) => (
                                                                <div key={itemIndex} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                                                                    <CheckCircle className={`h-5 w-5 text-${selectedSection.color}-500 mt-0.5 flex-shrink-0`} />
                                                                    <span className="text-sm text-gray-700">{item}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            // Render original layout for simple list of features
                                            <div className="grid md:grid-cols-2 gap-4">
                                                {selectedSection.content.features.map((feature, index) => (
                                                    <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                                                        <CheckCircle className={`h-5 w-5 text-${selectedSection.color}-600 mt-0.5 flex-shrink-0`} />
                                                        <span className="text-gray-800">{feature}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Workflow Diagrams */}
                                {selectedSection.content.workflow && selectedSection.id !== 'overview' && (
                                    <DiagramBlock title="Workflow Diagram" type="workflow">
                                        <div className="whitespace-pre-line text-gray-700 font-mono text-sm bg-gray-50 p-4 rounded">
                                            {selectedSection.content.workflow}
                                        </div>
                                    </DiagramBlock>
                                )}

                                {/* Code Examples */}
                                {selectedSection.content.examplePrompt && (
                                    <ExampleBlock title="Example AI Prompt" language="text" downloadable={true}>
                                        <div className="whitespace-pre-line text-gray-700">
                                            {selectedSection.content.examplePrompt}
                                        </div>
                                    </ExampleBlock>
                                )}

                                {selectedSection.content.excelFormat && (
                                    <ExampleBlock title="Required Excel Format" language="text" downloadable={true}>
                                        <div className="whitespace-pre-line text-gray-700 font-mono">
                                            {selectedSection.content.excelFormat}
                                        </div>
                                    </ExampleBlock>
                                )}

                                {selectedSection.content.gradeScaleExample && (
                                    <ExampleBlock title="Grade Scale Example" language="text" downloadable={true}>
                                        <div className="whitespace-pre-line text-gray-700 font-mono">
                                            {selectedSection.content.gradeScaleExample}
                                        </div>
                                    </ExampleBlock>
                                )}

                                {/* Special Content Sections */}
                                {selectedSection.content.achievementLevels && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Achievement Level Options</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.achievementLevels.map((level, index) => (
                                                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                                                    <h4 className="font-medium text-gray-900 mb-2">{level.level}-Point Scale</h4>
                                                    <p className="text-gray-600 text-sm">{level.name}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.exportOptions && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Export Options</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.exportOptions.map((option, index) => (
                                                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                                                    <h4 className="font-medium text-gray-900 mb-2">{option.name}</h4>
                                                    <p className="text-gray-600 text-sm mb-2">{option.description}</p>
                                                    <p className="text-gray-500 text-xs">{option.content}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.exportFormats && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Export Formats</h3>
                                        <div className="grid md:grid-cols-3 gap-4">
                                            {selectedSection.content.exportFormats.map((format, index) => (
                                                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                                                    <h4 className="font-medium text-gray-900 mb-2">{format.format}</h4>
                                                    <p className="text-gray-600 text-sm">{format.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.programTypes && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Supported Program Types</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.programTypes.map((type, index) => (
                                                <div key={index} className="p-4 border border-teal-200 rounded-lg bg-teal-50">
                                                    <h4 className="font-medium text-teal-900 mb-2">{type.type}</h4>
                                                    <p className="text-teal-800 text-sm">{type.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.supportedFormats && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Supported File Formats</h3>
                                        <div className="grid md:grid-cols-3 gap-4">
                                            {selectedSection.content.supportedFormats.map((format, index) => (
                                                <div key={index} className="p-4 border border-pink-200 rounded-lg bg-pink-50">
                                                    <h4 className="font-medium text-pink-900 mb-2">{format.format}</h4>
                                                    <p className="text-pink-800 text-sm">{format.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.guidelines && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Guidelines</h3>
                                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <div className="flex items-start space-x-2">
                                                <Lightbulb className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <ul className="space-y-2">
                                                        {selectedSection.content.guidelines.map((guideline, index) => (
                                                            <li key={index} className="text-yellow-800 text-sm">• {guideline}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.integrationPoints && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Integration Points</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.integrationPoints.map((point, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                                                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-green-900">{point}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.validationChecks && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Validation Checks</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.validationChecks.map((check, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-4 bg-emerald-50 rounded-lg">
                                                    <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-emerald-900">{check}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.testingMetrics && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Testing Metrics</h3>
                                        <div className="grid md:grid-cols-3 gap-4">
                                            {selectedSection.content.testingMetrics.map((metric, index) => (
                                                <div key={index} className="p-4 border border-gray-200 rounded-lg text-center">
                                                    <BarChart3 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                                                    <p className="text-sm font-medium text-gray-900">{metric}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.privacyCompliance && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Privacy Compliance Features</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.privacyCompliance.map((feature, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                                                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-green-900">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.sessionManagement && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Session Management</h3>
                                        <div className="space-y-3">
                                            {selectedSection.content.sessionManagement.map((item, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                                                    <Timer className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-blue-900">{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.dataHandling && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Handling</h3>
                                        <div className="space-y-3">
                                            {selectedSection.content.dataHandling.map((item, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg">
                                                    <Database className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-purple-900">{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.complianceChecklist && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-6">GDPR Compliance Checklist</h3>
                                        <div className="p-6 border border-green-200 rounded-lg bg-green-50">
                                            <div className="grid md:grid-cols-2 gap-4">
                                                {selectedSection.content.complianceChecklist.map((item, index) => (
                                                    <div key={index} className="flex items-start space-x-2">
                                                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                        <span className="text-green-800 text-sm">{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.userRights && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-6">User Rights Implementation</h3>
                                        <div className="space-y-4">
                                            {selectedSection.content.userRights.map((right, index) => (
                                                <div key={index} className="p-4 border border-purple-200 rounded-lg bg-purple-50">
                                                    <h4 className="font-medium text-purple-900 mb-2">{right.right}</h4>
                                                    <p className="text-purple-800 text-sm">{right.implementation}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.securityMeasures && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Measures</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.securityMeasures.map((measure, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-4 bg-red-50 rounded-lg">
                                                    <Shield className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-red-900">{measure}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.workflowRecommendations && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Workflow Recommendations</h3>
                                        <div className="space-y-6">
                                            {selectedSection.content.workflowRecommendations.map((phase, index) => (
                                                <div key={index} className="border border-yellow-200 rounded-lg bg-yellow-50 p-4">
                                                    <h4 className="font-medium text-yellow-900 mb-3">{phase.phase}</h4>
                                                    <ul className="space-y-2">
                                                        {phase.tasks.map((task, taskIndex) => (
                                                            <li key={taskIndex} className="flex items-start space-x-2">
                                                                <CheckCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                                <span className="text-yellow-800 text-sm">{task}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.timingTips && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Timing Recommendations</h3>
                                        <div className="space-y-3">
                                            {selectedSection.content.timingTips.map((tip, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                                                    <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-blue-900">{tip}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedSection.content.qualityAssurance && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Quality Assurance</h3>
                                        <div className="space-y-3">
                                            {selectedSection.content.qualityAssurance.map((item, index) => (
                                                <div key={index} className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                                                    <Award className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-green-900">{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Troubleshooting Section */}
                                {selectedSection.content.commonIssues && (
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-6">Common Issues & Solutions</h3>
                                        <div className="space-y-6">
                                            {selectedSection.content.commonIssues.map((issue, index) => (
                                                <div key={index} className="border border-red-200 rounded-lg">
                                                    <button
                                                        onClick={() => toggleSection(`issue-${index}`)}
                                                        className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 transition-colors duration-200 rounded-t-lg border-b border-red-200"
                                                    >
                                                        <div className="flex items-center space-x-3">
                                                            <AlertTriangle className="h-5 w-5 text-red-600" />
                                                            <span className="font-medium text-red-900">{issue.issue}</span>
                                                        </div>
                                                        <ChevronDown className={`h-5 w-5 text-red-600 transition-transform duration-200 ${expandedSections[`issue-${index}`] ? 'rotate-180' : ''
                                                            }`} />
                                                    </button>
                                                    {expandedSections[`issue-${index}`] && (
                                                        <div className="p-4 bg-white space-y-4">
                                                            <div>
                                                                <h5 className="font-medium text-gray-900 mb-2">Symptoms:</h5>
                                                                <ul className="list-disc list-inside text-gray-700 text-sm">
                                                                    {issue.symptoms.map((symptom, sIndex) => (
                                                                        <li key={sIndex}>{symptom}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                            <div className="p-3 bg-green-100 border border-green-300 rounded-lg">
                                                                <div className="flex items-start space-x-2">
                                                                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <strong className="text-green-800">Solution:</strong>
                                                                        <p className="text-green-700 text-sm mt-1">{issue.solution}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                                                                <div className="flex items-start space-x-2">
                                                                    <Lightbulb className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <strong className="text-yellow-800">Prevention:</strong>
                                                                        <p className="text-yellow-700 text-sm mt-1">{issue.prevention}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Diagnostic Tools */}
                                {selectedSection.content.diagnosticTools && (
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Diagnostic Tools</h4>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {selectedSection.content.diagnosticTools.map((tool, index) => (
                                                <div key={index} className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                                                    <h5 className="font-medium text-blue-900 mb-2">{tool.name}</h5>
                                                    <p className="text-blue-800 text-sm mb-2">{tool.purpose}</p>
                                                    <p className="text-blue-700 text-xs font-mono bg-blue-100 p-2 rounded">{tool.howTo}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Emergency Procedures */}
                                {selectedSection.content.emergencyProcedures && (
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Emergency Procedures</h4>
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                            <div className="flex items-start space-x-2">
                                                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <ul className="space-y-2">
                                                        {selectedSection.content.emergencyProcedures.map((procedure, index) => (
                                                            <li key={index} className="text-red-800 text-sm">• {procedure}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional Resources Section */}
                                <div className="mt-12 pt-8 border-t border-gray-200">
                                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Additional Resources</h3>
                                    <div className="grid md:grid-cols-3 gap-6">
                                        <div className="p-6 border border-blue-200 rounded-lg bg-blue-50">
                                            <FileText className="h-8 w-8 text-blue-600 mb-3" />
                                            <h4 className="font-medium text-blue-900 mb-2">Documentation</h4>
                                            <p className="text-blue-800 text-sm mb-4">Comprehensive guides and technical documentation</p>
                                            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center">
                                                <ExternalLink className="h-4 w-4 mr-1" />
                                                View Docs
                                            </button>
                                        </div>

                                        <div className="p-6 border border-green-200 rounded-lg bg-green-50">
                                            <Globe className="h-8 w-8 text-green-600 mb-3" />
                                            <h4 className="font-medium text-green-900 mb-2">Video Tutorials</h4>
                                            <p className="text-green-800 text-sm mb-4">Step-by-step video guides for all features</p>
                                            <button className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center">
                                                <PlayCircle className="h-4 w-4 mr-1" />
                                                Watch Videos
                                            </button>
                                        </div>

                                        <div className="p-6 border border-purple-200 rounded-lg bg-purple-50">
                                            <HelpCircle className="h-8 w-8 text-purple-600 mb-3" />
                                            <h4 className="font-medium text-purple-900 mb-2">Support</h4>
                                            <p className="text-purple-800 text-sm mb-4">Get help from our support team</p>
                                            <button className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center">
                                                <Mail className="h-4 w-4 mr-1" />
                                                Contact Support
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Download Templates Section */}
                                <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-gray-200">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Download Templates</h4>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <button className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-200">
                                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                                            </div>
                                            <div className="text-left">
                                                <div className="font-medium text-gray-900">Class List Template</div>
                                                <div className="text-sm text-gray-600">Excel format with required columns</div>
                                            </div>
                                            <Download className="h-4 w-4 text-gray-400" />
                                        </button>

                                        <button className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors duration-200">
                                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                                <FileJson className="h-5 w-5 text-purple-600" />
                                            </div>
                                            <div className="text-left">
                                                <div className="font-medium text-gray-900">Rubric Template</div>
                                                <div className="text-sm text-gray-600">JSON format with sample structure</div>
                                            </div>
                                            <Download className="h-4 w-4 text-gray-400" />
                                        </button>
                                    </div>
                                </div>

                                {/* Version Info */}
                                <div className="mt-8 p-4 bg-gray-100 rounded-lg border border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                <Monitor className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">GradingPilot Professional Suite</div>
                                                <div className="text-sm text-gray-600">Version 2.0 - Enhanced Privacy & Performance</div>
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Last Updated: {new Date().toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Floating Help Button */}
            <div className="fixed bottom-6 right-6 z-50">
                <button
                    onClick={() => setSelectedCategory('overview')}
                    className="w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
                    title="Back to Overview"
                >
                    <HelpCircle className="h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
                </button>
            </div>
        </div>
    );
};

export default HelpPage;